'use client'

import { useState, useEffect, useCallback, useMemo } from 'react'
import { apiFetchWithRetry } from '@/lib/api'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { toast } from 'sonner'
import { Loader2, Calendar, Clock, FileText, ChevronDown, ChevronUp, Download, Users, Calendar as CalendarIcon, ClipboardList, TrendingUp, UserCheck } from 'lucide-react'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Button } from '@/components/ui/button'
import { DateRangeInput } from '@/components/ui/date-input'
import { exportDailyWorkToExcel, exportDailyWorkByEmployeeToExcel, type DailyWorkRecord } from '@/lib/excel-export'
import { formatDate, parseDateToISO } from '@/lib/utils'
import { useMultiOperations } from '@/hooks/useOperations'
import { User } from '@/types/user'
import { PageShell } from '@/components/ui/page-shell'
import { SectionHeader } from '@/components/ui/section-header'
import { GlassCard } from '@/components/ui/glass-card'
import { KpiTile } from '@/components/ui/kpi-tile'
import { StatusPill } from '@/components/ui/status-pill'
import { TabLoader, EmptyState } from '@/components/ui/loading'
import { PRIMARY_BTN } from '@/components/ui/button-styles'
import { formatISTDate } from '@/lib/date-utils'

export function DailyWorkView() {
  const [dailyWorks, setDailyWorks] = useState<DailyWorkRecord[]>([])
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [filterUser, setFilterUser] = useState<string>('all')
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [exportLoading, setExportLoading] = useState(false)
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({
    start: '',
    end: ''
  })
  const { states } = useMultiOperations()

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      let url = '/api/daily-work'
      const params = new URLSearchParams()

      if (filterUser && filterUser !== 'all') {
        params.append('userId', filterUser)
      }

      if (dateRange.start) {
        params.append('startDate', parseDateToISO(dateRange.start))
      }

      if (dateRange.end) {
        params.append('endDate', parseDateToISO(dateRange.end))
      }

      if (params.toString()) {
        url += `?${params.toString()}`
      }

      const [workResult, usersResult] = await Promise.allSettled([
        apiFetchWithRetry(url),
        apiFetchWithRetry('/api/users')
      ])

      if (workResult.status === 'fulfilled') {
        const workData = await workResult.value.json()
        if (workData.dailyWorks && Array.isArray(workData.dailyWorks)) {
          setDailyWorks(workData.dailyWorks)
        }
      } else {
        console.error('Error fetching daily work data:', workResult.reason)
        toast.error(workResult.reason?.message || 'Failed to fetch daily work data')
      }

      if (usersResult.status === 'fulfilled') {
        const usersData = await usersResult.value.json()
        if (usersData.users && Array.isArray(usersData.users)) {
          const showInactive = localStorage.getItem('sasha_show_inactive_users') === 'true'
          setUsers(usersData.users.filter((u: User) => u && u.role === 'EMPLOYEE' && (showInactive || u.isActive !== false)))
        }
      } else {
        console.error('Error fetching users:', usersResult.reason)
        toast.error(usersResult.reason?.message || 'Failed to fetch users')
      }
    } catch (e: any) {
      console.error('Error fetching daily work data:', e)
      toast.error(e.message || 'Failed to fetch daily work data')
    } finally {
      setLoading(false)
    }
  }, [filterUser, dateRange])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const kpis = useMemo(() => {
    const today = formatISTDate(new Date())
    const now = new Date()
    const day = now.getDay()
    const diffToMon = day === 0 ? -6 : 1 - day
    const weekStart = new Date(now)
    weekStart.setDate(now.getDate() + diffToMon)
    weekStart.setHours(0, 0, 0, 0)
    const loggedToday = dailyWorks.filter(w => w.date === today).length
    const thisWeek = dailyWorks.filter(w => new Date(w.date + 'T00:00:00') >= weekStart)
    const totalWeekHours = thisWeek.reduce((s, w) => s + (Number(w.hoursWorked) || 0), 0)
    const avg = dailyWorks.length ? Math.round((dailyWorks.reduce((s, w) => s + (Number(w.hoursWorked) || 0), 0) / dailyWorks.length) * 10) / 10 : 0
    const submitters = new Set(dailyWorks.map(w => w.user?.id)).size
    return { loggedToday, totalWeekHours: Math.round(totalWeekHours * 10) / 10, avg, submitters }
  }, [dailyWorks])

  const handleExportAll = async () => {
    setExportLoading(true)
    try {
      await exportDailyWorkToExcel({
        records: dailyWorks.map(w => ({
          ...w,
          user: {
            id: w.user.id,
            name: w.user.name,
            email: w.user.email || '',
            employeeId: w.user.employeeId ?? null,
            department: w.user.department ?? null,
          },
        })),
        filterType: 'all'
      })
    } catch (error) {
      console.error('Export failed:', error)
      toast.error('Failed to export daily work records')
    } finally {
      setExportLoading(false)
    }
  }

  const handleExportByEmployee = async () => {
    setExportLoading(true)
    try {
      const formattedRecords: DailyWorkRecord[] = dailyWorks.map(work => ({
        ...work,
        user: {
          id: work.user.id,
          name: work.user.name,
          email: work.user.email || '',
          employeeId: work.user.employeeId ?? null,
          department: work.user.department ?? null
        }
      }))
      await exportDailyWorkByEmployeeToExcel(formattedRecords)
    } catch (error) {
      console.error('Export failed:', error)
      toast.error('Failed to export daily work records')
    } finally {
      setExportLoading(false)
    }
  }

  const handleExportFiltered = async () => {
    setExportLoading(true)
    try {
      const filterType = filterUser && filterUser !== 'all' ? 'employee' :
                       dateRange.start || dateRange.end ? 'date' : 'all'

      await exportDailyWorkToExcel({
        records: dailyWorks.map(w => ({
          ...w,
          user: {
            id: w.user.id,
            name: w.user.name,
            email: w.user.email || '',
            employeeId: w.user.employeeId ?? null,
            department: w.user.department ?? null,
          },
        })),
        filterType,
        employeeName: filterUser && filterUser !== 'all' ? users.find(u => u.id === filterUser)?.name : undefined,
        dateRange: dateRange.start && dateRange.end ? dateRange : undefined
      })
    } catch (error) {
      console.error('Export failed:', error)
      toast.error('Failed to export daily work records')
    } finally {
      setExportLoading(false)
    }
  }

  const clearFilters = () => {
    setFilterUser('all')
    setDateRange({ start: '', end: '' })
  }

  return (
    <PageShell>
      <SectionHeader
        icon={ClipboardList}
        title="Daily Work Updates"
        description="View employee daily work logs"
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiTile label="Logged Today" value={kpis.loggedToday} icon={Calendar} tone="orange" />
        <KpiTile label="Total Hours (week)" value={kpis.totalWeekHours} icon={Clock} tone="blue" format={(n) => `${n}h`} />
        <KpiTile label="Avg Hours / Log" value={kpis.avg} icon={TrendingUp} tone="green" format={(n) => `${n}h`} />
        <KpiTile label="Submitters" value={kpis.submitters} icon={UserCheck} tone="violet" />
      </div>

      <GlassCard inset>
        <div className="p-4 md:p-5 flex flex-col lg:flex-row lg:items-center gap-3">
          <div className="flex flex-col sm:flex-row gap-2 flex-1">
            <Select value={filterUser} onValueChange={setFilterUser}>
              <SelectTrigger className="w-full sm:w-[220px]">
                <SelectValue placeholder="All Employees" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Employees</SelectItem>
                {users.map((user) => (
                  <SelectItem key={user.id} value={user.id}>{user?.name || 'Unknown'}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <DateRangeInput
              startDate={dateRange.start}
              endDate={dateRange.end}
              onStartDateChange={(start) => setDateRange({ ...dateRange, start })}
              onEndDateChange={(end) => setDateRange({ ...dateRange, end })}
              className="w-full sm:w-[260px]"
            />
            <Button
              variant="outline"
              onClick={clearFilters}
              className="text-sm border-neutral-200 dark:border-white/10 text-neutral-700 dark:text-white/70"
            >
              Clear
            </Button>
          </div>

          <div className="flex flex-wrap gap-2 lg:justify-end">
            <Button
              onClick={handleExportAll}
              disabled={exportLoading || dailyWorks.length === 0 || states.exportAll?.loading}
              className={PRIMARY_BTN}
            >
              {exportLoading || states.exportAll?.loading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Export All
            </Button>
            <Button
              onClick={handleExportByEmployee}
              disabled={exportLoading || dailyWorks.length === 0 || states.exportByEmployee?.loading}
              variant="outline"
              className="border-neutral-200 dark:border-white/10 text-neutral-800 dark:text-white/80"
            >
              {exportLoading || states.exportByEmployee?.loading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Users className="h-4 w-4 mr-2" />
              )}
              By Employee
            </Button>
            <Button
              onClick={handleExportFiltered}
              disabled={exportLoading || dailyWorks.length === 0 || states.exportFiltered?.loading}
              variant="outline"
              className="border-neutral-200 dark:border-white/10 text-neutral-800 dark:text-white/80"
            >
              {exportLoading || states.exportFiltered?.loading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <CalendarIcon className="h-4 w-4 mr-2" />
              )}
              Filtered
            </Button>
          </div>
        </div>
      </GlassCard>

      <GlassCard>
        {loading ? (
          <TabLoader />
        ) : dailyWorks.length === 0 ? (
          <EmptyState
            icon={ClipboardList}
            title="No daily work logs to display"
            description="Adjust the employee or date-range filters, or wait for employees to submit logs."
          />
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block">
              <ScrollArea className="h-[520px]">
                <div className="min-w-[760px]">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-neutral-200 dark:border-white/10 hover:bg-transparent">
                        <TableHead className="text-neutral-500 dark:text-white/50">Employee</TableHead>
                        <TableHead className="text-neutral-500 dark:text-white/50">Date</TableHead>
                        <TableHead className="text-neutral-500 dark:text-white/50">Work Title</TableHead>
                        <TableHead className="text-neutral-500 dark:text-white/50">Description</TableHead>
                        <TableHead className="text-neutral-500 dark:text-white/50">Hours</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {dailyWorks.map((work) => {
                        const isExpanded = expandedId === work.id
                        return (
                          <TableRow key={work.id} className="border-neutral-100 dark:border-white/5 hover:bg-neutral-50 dark:hover:bg-white/5 cursor-pointer" onClick={() => setExpandedId(isExpanded ? null : work.id)}>
                            <TableCell>
                              <p className="text-neutral-900 dark:text-white/90 font-medium">{work.user?.name || 'Unknown'}</p>
                              <p className="text-xs text-neutral-400 dark:text-white/40">{work.user?.department || work.user?.employeeId || '-'}</p>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1.5 text-neutral-800 dark:text-white/80 text-sm">
                                <Calendar className="h-3.5 w-3.5 text-neutral-400 dark:text-white/40" />
                                {formatDate(work.date)}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <FileText className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                                <span className="text-neutral-900 dark:text-white/90">{work.title}</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-neutral-700 dark:text-white/70 max-w-[420px]">
                              <p className={isExpanded ? 'whitespace-pre-wrap' : 'truncate'}>{work.description}</p>
                              {work.description && work.description.length > 50 && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300 p-0 h-auto text-xs mt-1"
                                  onClick={(e) => { e.stopPropagation(); setExpandedId(isExpanded ? null : work.id) }}
                                >
                                  {isExpanded ? (
                                    <><ChevronUp className="h-3 w-3 mr-1" /> Show less</>
                                  ) : (
                                    <><ChevronDown className="h-3 w-3 mr-1" /> Show more</>
                                  )}
                                </Button>
                              )}
                            </TableCell>
                            <TableCell>
                              <StatusPill tone="blue">
                                <Clock className="h-3 w-3" />
                                {work.hoursWorked}h
                              </StatusPill>
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                </div>
              </ScrollArea>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden space-y-3">
              {dailyWorks.map((work) => {
                const isExpanded = expandedId === work.id
                return (
                  <GlassCard key={work.id} interactive onClick={() => setExpandedId(isExpanded ? null : work.id)}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-neutral-900 dark:text-white/90 font-medium truncate">{work.user?.name || 'Unknown'}</p>
                        <p className="text-xs text-neutral-400 dark:text-white/40">{work.user?.department || work.user?.employeeId || '-'}</p>
                      </div>
                      <StatusPill tone="blue">
                        <Clock className="h-3 w-3" />
                        {work.hoursWorked}h
                      </StatusPill>
                    </div>
                    <div className="mt-2 flex items-center gap-2 text-xs text-neutral-600 dark:text-white/60">
                      <Calendar className="h-3 w-3" />
                      {formatDate(work.date)}
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <FileText className="h-4 w-4 text-orange-600 dark:text-orange-400 shrink-0" />
                      <span className="text-neutral-900 dark:text-white/90 text-sm truncate">{work.title}</span>
                    </div>
                    {work.description && (
                      <p className={`mt-2 text-sm text-neutral-700 dark:text-white/70 ${isExpanded ? 'whitespace-pre-wrap' : 'line-clamp-2'}`}>
                        {work.description}
                      </p>
                    )}
                  </GlassCard>
                )
              })}
            </div>
          </>
        )}
      </GlassCard>
    </PageShell>
  )
}
